import React, { useCallback,useEffect, useState } from "react";

import { useLocation } from "react-router-dom";

import { PAGE_SECTIONS } from "./HeaderWrapper";

export const SectionDotNav: React.FC = () => {
  const location = useLocation();
  const sections = PAGE_SECTIONS[location.pathname] ?? [];
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");
  const [visible, setVisible] = useState(false);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Show after scrolling down a bit
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is in view
  const updateActive = useCallback(() => {
    const offsets = sections
      .map(({ id }) => {
        const el = document.getElementById(id);
        if (!el) return null;
        return { id, top: el.getBoundingClientRect().top };
      })
      .filter(Boolean) as { id: string; top: number }[];

    // Find the last section that has scrolled past the 40% mark
    const passed = offsets.filter((s) => s.top <= window.innerHeight * 0.4);
    if (passed.length > 0) {
      setActiveId(passed[passed.length - 1].id);
    } else if (offsets.length > 0) {
      setActiveId(offsets[0].id);
    }
  }, [sections]);

  useEffect(() => {
    window.addEventListener("scroll", updateActive, { passive: true });
    updateActive();
    return () => window.removeEventListener("scroll", updateActive);
  }, [updateActive]);

  // Reset active on page change
  useEffect(() => {
    setActiveId(sections[0]?.id ?? "");
  }, [location.pathname]);

  if (sections.length === 0) return null;

  return (
    <div
      className={`
        hidden sm:flex
        fixed right-5 top-1/2 -translate-y-1/2 z-50
        flex flex-col items-center gap-3
        transition-all duration-500
        ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6 pointer-events-none"}
      `}
    >
      {/* Background pill */}
      <div className="
        absolute inset-x-0 inset-y-[-12px]
        bg-white/80 backdrop-blur-sm
        rounded-full
        shadow-[0_4px_24px_rgba(0,0,0,0.10)]
        border border-white/60
      " />

      {sections.map((sec) => {
        const isActive = activeId === sec.id;
        return (
          <button
            key={sec.id}
            onClick={() => scrollToSection(sec.id)}
            className="relative group flex items-center justify-center"
            aria-label={`Jump to ${sec.label}`}
          >
            {/* Dot */}
            <span
              className={`
                relative z-10 block rounded-full transition-all duration-300
                ${isActive
                  ? "w-2.5 h-2.5 bg-red-600 shadow-[0_0_8px_rgba(220,0,0,0.5)]"
                  : "w-1.5 h-1.5 bg-neutral-300 group-hover:bg-neutral-500"
                }
              `}
            />

            {/* Tooltip label */}
            <span
              className="
                absolute right-full mr-3
                px-2.5 py-1.5
                bg-neutral-900/90 backdrop-blur-sm
                text-white text-[11px] font-medium
                rounded-[8px]
                whitespace-nowrap
                opacity-0 group-hover:opacity-100
                translate-x-1 group-hover:translate-x-0
                transition-all duration-200
                pointer-events-none
                shadow-lg
              "
            >
              {sec.label}
              {/* Arrow */}
              <span className="
                absolute left-full top-1/2 -translate-y-1/2
                border-4 border-transparent
                border-l-neutral-900/90
              " />
            </span>
          </button>
        );
      })}
    </div>
  );
}